import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuperAdminStudentGroups, SAStudentGroups } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const TIER_CLS: Record<string, string> = { bright: 'bright', ontrack: 'ontrack', support: 'support', uncat: 'uncat' };

/** Student groups (cohorts) — TVG stream 2. Tiers from the sticky
 *  student_category (real); movement ships dark until the snapshot cron (task 150). */
const SuperAdminStudentGroups: React.FC = () => {
  const navigate = useNavigate();
  const [d, setD] = useState<SAStudentGroups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminStudentGroups(token)
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  return (
    <SuperAdminLayout>
      <div className="content">
        <h1 className="pg">Student groups</h1>
        <p className="pg-sub">
          The brightest and those who need support, grouped by the coach's read of each student.
          Grouping is AI-assessed; a baseline quiz (task 152) will anchor it at launch.
        </p>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error || !d ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error || 'Not found.'}</p>
        ) : (
          <>
            <div className="modtag"><span className="rd rd-part" /><span className="nm">The groups</span> · {d.categorised} of {d.total_students} students grouped</div>
            <div className="grid g4">
              {d.tiers.map((t) => (
                <div key={t.key} className={`tier ${TIER_CLS[t.key] || ''}`}>
                  <div className="cap">{t.label}</div>
                  <div className="big">{t.count.toLocaleString()}</div>
                  <div className="pct">{t.pct}% · {t.note}</div>
                </div>
              ))}
            </div>

            <div className="grid g2e mt14">
              <div className="card">
                <div className="modtag"><span className="rd rd-cap" /><span className="nm">Group changes</span> · week to week</div>
                <h3>Who moved between groups</h3>
                <p className="sub">Feeds prompt learnings — “what did we do right?”</p>
                <div className="tile dark-ship" style={{ display: 'grid', placeItems: 'center', minHeight: 90, textAlign: 'center' }}>
                  <div>
                    <div className="val" style={{ fontSize: 18 }}>—</div>
                    <span className="capture">needs the weekly snapshot cron (task 150)</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="modtag"><span className="rd rd-ready" /><span className="nm">Needs support</span> · watchlist</div>
                <h3>Who to check on <span style={{ fontWeight: 500, fontSize: 11, color: 'var(--faint)' }}>click to open</span></h3>
                {d.watch.length === 0 ? (
                  <p className="hint">No one flagged “needs support” yet.</p>
                ) : (
                  <div className="ivl">
                    {d.watch.map((w) => (
                      <div key={w.student_id} className="iv" onClick={() => navigate(`/super-admin/students/${w.student_id}`)}>
                        <span className="sig crit" />
                        <span className="what"><b>{w.name}</b><small>{w.detail}</small></span>
                        <span className="modref">support</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="note w">
              <b>Post-launch (TVG stream 2).</b> Tiers are live now from the coach's student read; the trustworthy anchor is a baseline quiz in sessions 1–3 (task 152), and week-to-week movement needs the per-student snapshot cron (task 150).
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStudentGroups;
